import React, { useState } from 'react';
import { X, Info, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const Required = () => <span className="text-red-500 ml-1">*</span>;

const Accordion = ({ title, children, open, onToggle }) => (
  <div className="border rounded-lg">
    <button 
      onClick={onToggle}
      className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100"
    >
      <h3 className="font-medium text-left">{title}</h3>
      {open ? <ChevronUp /> : <ChevronDown />}
    </button>
    {open && <div className="p-4 border-t">{children}</div>}
  </div>
);

const TEKSEntry = ({ index, handleChange, data }) => (
  <div className="space-y-4 p-4 bg-white rounded-lg shadow-sm">
    <div>
      <label className="block text-sm font-medium mb-1">Knowledge and Skills <Required /></label>
      <textarea 
        rows="2"
        className="w-full p-2 border rounded resize-none"
        value={data.knowledge || ''}
        onChange={(e) => handleChange(index, 'knowledge', e.target.value)}
      />
    </div>
    <div>
      <label className="block text-sm font-medium mb-1">Work-Based Instruction <Required /></label>
      <textarea 
        rows="2"
        className="w-full p-2 border rounded resize-none"
        value={data.instruction || ''}
        onChange={(e) => handleChange(index, 'instruction', e.target.value)}
      />
    </div>
    <div>
      <label className="block text-sm font-medium mb-1">Individualized Class Study</label>
      <textarea 
        rows="2"
        className="w-full p-2 border rounded resize-none"
        value={data.study || ''}
        onChange={(e) => handleChange(index, 'study', e.target.value)}
      />
    </div>
    <div>
      <label className="block text-sm font-medium mb-1">Related Study Assignments</label>
      <textarea 
        rows="2"
        className="w-full p-2 border rounded resize-none"
        value={data.assignments || ''}
        onChange={(e) => handleChange(index, 'assignments', e.target.value)}
      />
    </div>
  </div>
);

const TermsAccordion = ({ open, onToggle }) => (
  <Accordion 
    title="Training Agreement Terms" 
    open={open} 
    onToggle={onToggle}
  >
    <div className="space-y-4 text-sm">
      <div>
        <h4 className="font-medium mb-2">Student Agreement</h4>
        <p>The student agrees to diligently perform the work-based training experiences and conscientiously pursue the coordinated classroom course of study as outlined in this training plan. Work-based training experiences will be assigned by the training sponsor and performed according to the same company policies and regulations applicable to regular employees.</p>
      </div>
      <div>
        <h4 className="font-medium mb-2">Company and School Responsibilities</h4>
        <p>The company and school are responsible for providing students with opportunities for training in the basic skills of an occupation and knowledge of related technical information. In order to provide a systematic plan for well-rounded training, a schedule of work-based training experiences and a parallel classroom course of study have been coordinated and agreed upon by the training sponsor and CTE teacher.</p>
      </div>
      <div>
        <h4 className="font-medium mb-2">Non-Discrimination Policy</h4>
        <p>It is the policy of School District not to discriminate on the basis of race, color, national origin, sex or disability in its Career and Technical Education programs, services or activities as required by Title VI of the Civil Rights Act of 1964, as amended; Title IX of the Education Amendments of 1972; and Section 504 of the Rehabilitation Act of 1973, as amended.</p>
        <p className="mt-2">Es norma de Distrito Escolar no discriminar por motivos de raza, color, origen nacional, sexo o impedimento, en sus programas, servicios o actividades vocacionales, tal como lo requieren el Título VI de la Ley de Derechos Civiles de 1964, según enmienda; el Título IX de las Enmiendas en la Educación, de 1972, y la Sección 504 de la Ley de Rehabilitación de 1973, según enmienda.</p>
      </div>
    </div>
  </Accordion>
);

const ProfileModal = ({ onClose, student }) => {
  const [step, setStep] = useState(1);
  const [openAccordions, setOpenAccordions] = useState({});
  const [teksEntries, setTeksEntries] = useState([{}]);
  const [formData, setFormData] = useState({
    studentInfo: {
      name: student ? `${student.firstName} ${student.lastName}` : '',
      grade: '',
      age: '',
      schoolDistrict: '',
    },
    trainingDetails: {},
    skills: {},
    signatures: {}
  });

  const toggleAccordion = (id) => {
    setOpenAccordions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleTEKSChange = (index, field, value) => {
    const newEntries = [...teksEntries];
    newEntries[index] = {
      ...newEntries[index],
      [field]: value
    };
    setTeksEntries(newEntries);
  };

  const renderStep = () => {
    switch(step) {
      case 1:
        return (
          <div className="space-y-6">
            <Accordion 
              title="Basic Information" 
              open={openAccordions.basic} 
              onToggle={() => toggleAccordion('basic')}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Student <Required />
                  </label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded" 
                    value={formData.studentInfo.name}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Grade <Required />
                  </label>
                  <input type="text" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Age <Required />
                  </label>
                  <input type="number" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    School District <Required />
                  </label>
                  <input type="text" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Campus Name <Required />
                  </label>
                  <input type="text" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Training Objective <Required />
                  </label>
                  <input type="text" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Business Name <Required />
                  </label>
                  <input type="text" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Career Cluster <Required />
                  </label>
                  <input type="text" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    CTE Course Title <Required />
                  </label>
                  <input type="text" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Time Class Meets <Required />
                  </label>
                  <input type="text" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    PEIMS Code <Required />
                  </label>
                  <input type="text" className="w-full p-2 border rounded" />
                </div>
              </div>
            </Accordion>

            <Accordion 
              title="Wage Information" 
              open={openAccordions.wage}
              onToggle={() => toggleAccordion('wage')}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Beginning wage ($) <Required />
                    </label>
                    <input type="number" step="0.01" className="w-full p-2 border rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Per <Required />
                    </label>
                    <select className="w-full p-2 border rounded">
                      <option value="">Select period</option>
                      <option value="hour">Hour</option>
                      <option value="day">Day</option>
                      <option value="week">Week</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Hours per school week <Required />
                    </label>
                    <input type="number" className="w-full p-2 border rounded" />
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Periodically, the training sponsor and CTE teacher will jointly review the wages paid to determine a fair and equitable wage consistent with the student's increased ability, prevailing economic conditions, and company policy.
                </p>
              </div>
            </Accordion>

            <Accordion 
              title="Training Period" 
              open={openAccordions.period}
              onToggle={() => toggleAccordion('period')}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Start Date <Required />
                  </label>
                  <input type="date" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    End Date <Required />
                  </label>
                  <input type="date" className="w-full p-2 border rounded" />
                </div>
              </div>
            </Accordion>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <Accordion 
              title="TEKS Requirements" 
              open={openAccordions.teks}
              onToggle={() => toggleAccordion('teks')}
            >
              <div className="space-y-4">
                {teksEntries.map((entry, index) => (
                  <TEKSEntry 
                    key={index}
                    index={index}
                    handleChange={handleTEKSChange}
                    data={entry}
                  />
                ))}
                <button
                  onClick={() => setTeksEntries([...teksEntries, {}])}
                  className="text-blue-600 hover:text-blue-700"
                >
                  + Add Another Entry
                </button>
              </div>
            </Accordion>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <Accordion 
              title="Signatures" 
              open={openAccordions.signatures}
              onToggle={() => toggleAccordion('signatures')}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Student Signature <Required />
                  </label>
                  <div className="border rounded h-20 bg-gray-50 mb-2"></div>
                  <input type="date" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Parent/Guardian Signature <Required />
                  </label>
                  <div className="border rounded h-20 bg-gray-50 mb-2"></div>
                  <input type="date" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Training Sponsor Signature <Required />
                  </label>
                  <div className="border rounded h-20 bg-gray-50 mb-2"></div>
                  <input type="date" className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    CTE Teacher Signature <Required />
                  </label>
                  <div className="border rounded h-20 bg-gray-50 mb-2"></div>
                  <input type="date" className="w-full p-2 border rounded" />
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600 bg-gray-50 p-4 rounded">
                Note: Each party should retain a signed copy. Original document will be kept with student's permanent record.
              </div>
            </Accordion>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={(e) => e.stopPropagation()}>
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 my-2 flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
        <div className="flex-none border-b px-6 py-3">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Training Plan & Certification</h2>
            <div className="flex gap-2">
              <a 
                href="/unpaid-training-plan.pdf"
                download
                className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 border border-blue-600 rounded flex items-center gap-1"
              >
                Download PDF Template
              </a>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-none px-6 py-2 border-b bg-white">
          <div className="flex justify-between items-center">
            <div className="flex space-x-2">
              {[1, 2, 3].map((num) => (
                <div
                  key={num}
                  className={`w-2 h-2 rounded-full 
                    ${step >= num ? 'bg-blue-600' : 'bg-gray-200'}`}
                />
              ))}
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <span>Certification:</span>
              <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Step {step} of 3: {
              step === 1 ? 'Basic Information' :
              step === 2 ? 'TEKS Requirements' :
              'Signatures'
            }
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {renderStep()}
        </div>

        <div className="flex-none border-t px-6 py-3 bg-white">
          <div className="flex justify-between">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50"
              >
                Previous
              </button>
            )}
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ml-auto"
              >
                Next
              </button>
            ) : (
              <button
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 ml-auto"
              >
                Submit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TrainingPlanForm = ({ student, onClose }) => {
  return <ProfileModal onClose={onClose} student={student} />;
};

export default TrainingPlanForm;